import React, { useState } from 'react';
import '../styles/Contact.css';
import useMetaTags from '../hooks/useMetaTags';

const Contact = () => {
  useMetaTags({
    title: 'Contact Us - KTP Beauty Clinic',
    description: 'Contact KTP Beauty Clinic for inquiries, appointments, and beauty services. Reach out to us via phone or our contact form.',
    canonicalUrl: 'https://ktpbeautyclinic.co.uk/contact'
  });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    website: '' // honeypot field
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check if honeypot field is filled (bot detected)
    if (formData.website) {
      console.log('Honeypot detected potential spam');
      setSubmitStatus('success'); // Pretend success to fool bots
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);
    
    // Determine if we're in development or production
    const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const apiUrl = isDevelopment 
      ? 'http://localhost:3001/api/send-email' 
      : '/api/send-email';  // Use relative path in production
    
    try {
      console.log('Submitting form to:', apiUrl);
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(formData),
        mode: 'cors'
      });

      let data;
      try {
        data = await response.json();
        console.log('Server response:', data);
      } catch (jsonError) {
        console.error('Error parsing JSON response:', jsonError);
        throw new Error('Invalid server response');
      }

      if (response.ok && (data.status === 'success' || data.message === 'Email sent successfully')) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', message: '', website: '' });
      } else {
        console.error('Server error:', data);
        throw new Error(data.message || data.error || data.details || 'Failed to send message');
      }
    } catch (error) {
      console.error('Error in form submission:', error);
      if (!navigator.onLine) {
        setSubmitStatus('offline');
      } else if (error.message.includes('Invalid server response')) {
        setSubmitStatus('server');
      } else if (error.message.includes('Failed to fetch')) {
        setSubmitStatus('network');
      } else {
        setSubmitStatus('error');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <h1>Contact Us</h1>
      <div className="contact-container">
        <div className="contact-info">
          <h2>Get in Touch</h2>
          <div className="info-section">
            <h3>Opening Hours</h3>
            <p>Our typical hours are listed below, but it's always best to check in before hand, or book an appointment online.</p>
            <p>Monday - Saturday: 10am - 7pm</p>
            <p>Sunday: Closed</p>
          </div>
          <div className="info-section">
            <h3>Contact</h3>
            <p>Phone: +44 161 494 6392</p>
            <p>Address: 126 Higher Bents Ln<br />Bredbury, Stockport<br />SK6 2LU</p>
          </div>
          <div className="info-section">
            <h3>Follow Us</h3>
            <div className="social-links">
              <a href="https://www.instagram.com/ktpbeautyclinic/" target="_blank" rel="noopener noreferrer">
                <img src="/images/instagram.svg" alt="Instagram" />
              </a>
            </div>
          </div>
        </div>
        
        <div className="contact-form">
          <h2>Send us a Message</h2>
          <form onSubmit={handleSubmit}>
            {/* Honeypot field - hidden from real users */}
            <div style={{ opacity: 0, position: 'absolute', top: 0, left: 0, height: 0, width: 0, zIndex: -1 }}>
              <label htmlFor="website">Website</label>
              <input
                type="text"
                id="website"
                name="website"
                value={formData.website}
                onChange={handleChange}
                tabIndex="-1"
                autoComplete="off"
              />
            </div>

            <div className="form-group">
              <label htmlFor="name">Name</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="phone">Phone (optional)</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="message">Message</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                rows="5"
              />
            </div>
            
            <button 
              type="submit" 
              className="submit-button"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </button>
            
            {submitStatus === 'success' && (
              <p className="success-message">Message sent successfully!</p>
            )}
            {submitStatus === 'error' && (
              <p className="error-message">Failed to send message. Please try again.</p>
            )}
            {submitStatus === 'offline' && (
              <p className="error-message">You appear to be offline. Please check your internet connection and try again.</p>
            )}
            {submitStatus === 'network' && (
              <p className="error-message">Network error. Please check your connection and try again.</p>
            )}
            {submitStatus === 'server' && (
              <p className="error-message">Server error. Please try again later.</p>
            )}
          </form>
        </div>
      </div>

      <footer className="footer">
        <div className="footer-content">
          <div className="footer-section">
            <h4>Contact</h4>
            <p>126 Higher Bents Ln<br />Bredbury, Stockport<br />SK6 2LU</p>
            <p><a href="tel:+44161494392">+44 161 494 6392</a></p>
          </div>
          
          <div className="footer-section">
            <h4>Opening Hours</h4>
            <p>Our typical hours are listed below, but it's always best to check in before hand, or book an appointment online.</p>
            <p>Monday - Saturday: 10am - 7pm<br />Sunday: Closed</p>
          </div>

          <div className="footer-section">
            <h4>Reviews</h4>
            <div className="rating">
              <p>
                <span className="stars">⭐⭐⭐⭐⭐</span>
                <br />
                <span className="rating-link">4.8 on Google Reviews</span>
              </p>
            </div>
            <a href="https://www.instagram.com/ktpbeautyclinic/" className="social-link" target="_blank" rel="noopener noreferrer">
              <img src="/images/instagram.svg" alt="Follow us on Instagram" />
            </a>
          </div>
        </div>
        <div className="footer-copyright">
          <p>&copy; {new Date().getFullYear()} KTP Beauty Clinic. All rights reserved.</p>
        </div>
      </footer>
    </>
  );
};

export default Contact;
